/**
 * External Link Click Tracking Service
 *
 * Provides fire-and-forget click tracking with optional authentication.
 * - Authenticated users: tracked with user ID via fetch() with JWT token
 * - Anonymous users: tracked without user ID via sendBeacon()
 */

import { authService } from './authService';
import type { EntityTypeSingular } from '../types/entities';

interface TrackLinkClickParams {
  entityType: EntityTypeSingular;
  entityId: string;
  platform: 'lego' | 'rebrickable' | 'bricklink' | 'brickeconomy' | 'brickowl' | 'brickset';
  url: string;
  conversationId?: string;
  referrer?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const TRACK_CLICK_ENDPOINT = `${API_BASE_URL}/api/entities/track-click`;

/**
 * Track an external link click asynchronously.
 *
 * For authenticated users, uses fetch() with JWT token to associate click with user.
 * For anonymous users, uses sendBeacon() for reliable fire-and-forget tracking.
 *
 * Errors are logged but don't prevent navigation - tracking failures should be silent.
 *
 * @param params - Click tracking parameters
 */
export function trackLinkClick(params: TrackLinkClickParams): void {
  try {
    const payload = JSON.stringify(params);
    const token = authService.getToken();

    // If user is authenticated, use fetch with JWT token to track with user ID
    if (token) {
      fetch(TRACK_CLICK_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: payload,
        // keepalive ensures request completes even if page unloads
        keepalive: true,
      }).catch((error) => {
        // Silently log errors - don't break user experience
        console.debug('Link click tracking failed (authenticated):', error);
      });

      console.debug('Link click tracked with auth:', params.platform, params.entityId);
      return;
    }

    // For anonymous users, try sendBeacon first (preferred for reliability)
    if (navigator.sendBeacon) {
      // sendBeacon requires a Blob for JSON content
      const blob = new Blob([payload], { type: 'application/json' });
      const sent = navigator.sendBeacon(TRACK_CLICK_ENDPOINT, blob);

      if (sent) {
        console.debug('Link click tracked via sendBeacon (anonymous):', params.platform, params.entityId);
        return;
      }
      // If sendBeacon fails (e.g., size limit), fall through to fetch
      console.debug('sendBeacon failed, falling back to fetch');
    }

    // Fallback to fetch() with keepalive flag for anonymous users
    fetch(TRACK_CLICK_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: payload,
      // keepalive ensures request completes even if page unloads
      keepalive: true,
    }).catch((error) => {
      // Silently log errors - don't break user experience
      console.debug('Link click tracking failed (anonymous):', error);
    });

    console.debug('Link click tracked via fetch (anonymous):', params.platform, params.entityId);
  } catch (error) {
    // Silently catch any errors - tracking should never break functionality
    console.debug('Link click tracking error:', error);
  }
}
